import { Chip, Skeleton, Stack, Typography } from '@mui/material';

import { HeaderField } from '~/components/common/HeaderField';
import { displayDate, formatDate } from '~/components/contractReview/contractDetailShared';
import type { ContractReview } from '~/components/contractReview/types';

/**
 * What the contract is — vendor, type, term, cancellation notice — and what the AI
 * pipeline made of it. Both AI fields say so in their labels: they are the model's
 * account of the contract rather than anything the contract itself states.
 *
 * In the header rather than the Overview tab: these never change and are the context
 * every tab is read against. Nothing is dropped from the drawer's Term and AI
 * Extraction cards — `Term` carries the contract's own term string as its caption.
 */
export function ContractIdentityFields({ contract }: { contract: ContractReview | null }) {
  const value = (text: string | number | null | undefined, fallback = '—') =>
    contract ? (
      <Typography variant="body2">{text || fallback}</Typography>
    ) : (
      <Skeleton width={110} />
    );

  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap columnGap={5} rowGap={1.5}>
      <HeaderField label="Vendor">{value(contract?.vendor)}</HeaderField>

      {/* No Type field: on this page the type is already the chip beside the contract's
          name, and two of the same word in one band reads as two different facts. */}

      <HeaderField label="Term" caption={contract?.term || undefined}>
        {contract ? (
          <Typography variant="body2">
            {displayDate(contract.startDate)} → {displayDate(contract.expirationDate)}
          </Typography>
        ) : (
          <Skeleton width={150} />
        )}
      </HeaderField>

      <HeaderField label="Cancellation">
        {value(
          contract?.cancellationDays !== null && contract?.cancellationDays !== undefined
            ? `${contract.cancellationDays} days notice`
            : null,
        )}
      </HeaderField>

      <HeaderField label="AI invoice review">
        {contract ? (
          <Chip
            size="small"
            color={contract.aiReviewEligible ? 'success' : 'default'}
            label={contract.aiReviewEligible ? 'Eligible' : 'Not eligible'}
            variant={contract.aiReviewEligible ? 'filled' : 'outlined'}
          />
        ) : (
          <Skeleton width={90} />
        )}
      </HeaderField>

      <HeaderField
        label="AI extraction"
        caption={
          contract?.extractionAppliedAt ? formatDate(contract.extractionAppliedAt) : undefined
        }
      >
        {value(
          contract?.extractionVersion ? `Pipeline ${contract.extractionVersion}` : null,
          'Not extracted',
        )}
      </HeaderField>
    </Stack>
  );
}
